import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, onSnapshot, query, doc, updateDoc, getDoc, orderBy, addDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    Check, X, Eye, ExternalLink,
    ChevronLeft, ChevronRight, Send, PlusCircle, Filter, Search, FileText
} from 'lucide-react';
import { notifySuccess, notifyError } from '../../utils/alerts';
import Swal from 'sweetalert2';
import { enviarPruebaWhatsApp } from '../../services/twilioTest';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { generarFactura, generarNombreFactura } from '../../utils/generarFactura';

const parseLocalDate = (value) => {
    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
};

const calcularFechaVencimiento = (fechaBase, dias) => {
    const fecha = parseLocalDate(fechaBase);
    fecha.setDate(fecha.getDate() + Number(dias));

    if (fecha.getMonth() === 11 || fecha.getMonth() === 0) {
        const year = fecha.getMonth() === 11 ? fecha.getFullYear() + 1 : fecha.getFullYear();
        return new Date(year, 1, 5);
    }

    return fecha;
};

const formatDateForStorage = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const GestionPagos = () => {
    const [pagos, setPagos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImg, setSelectedImg] = useState(null);
    const [selectedMotivo, setSelectedMotivo] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [allNinos, setAllNinos] = useState([]);
    // Total Pagos
    const [totalPagos, setTotalPagos] = useState(0);

    // ESTADOS PARA FILTROS
    const [filtroNombre, setFiltroNombre] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');

    const pagosPorPagina = 8;

    useEffect(() => {
        const q = query(collection(db, "pagos_recibidos"), orderBy("fechaEnvio", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPagos(docs);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const fetchNinos = async () => {
            const snap = await getDocs(collection(db, "ninos"));
            setAllNinos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchNinos();
    }, []);

    // --- LÓGICA DE FILTRADO ---
    const pagosFiltrados = pagos.filter(pago => {
        const matchesNombre = pago.ninoNombre.toLowerCase().includes(filtroNombre.toLowerCase());
        const matchesEstado = filtroEstado === 'todos' || pago.estado === filtroEstado;

        // Filtro de fecha
        const fechaPago = new Date(pago.fechaEnvio).toISOString().split('T')[0];
        const matchesFecha = (!fechaInicio || fechaPago >= fechaInicio) &&
            (!fechaFin || fechaPago <= fechaFin);

        return matchesNombre && matchesEstado && matchesFecha;
    });

    const indexOfLastPago = currentPage * pagosPorPagina;
    const indexOfFirstPago = indexOfLastPago - pagosPorPagina;
    const currentPagos = pagosFiltrados.slice(indexOfFirstPago, indexOfLastPago);
    const totalPages = Math.ceil(pagosFiltrados.length / pagosPorPagina);


    //calcular total de pagos
    useEffect(() => {
        const total = currentPagos.reduce((acc, pago) => acc + (Number(pago.monto) || 0), 0);
        setTotalPagos(total);

    }, [filtroEstado, filtroNombre, fechaInicio, fechaFin, currentPagos]);

    // --- REGISTRO DE PAGO MANUAL ---
    const handlePagoManual = async () => {
        const { value: formValues } = await Swal.fire({
            title: 'Registrar Pago Manual',
            html: `
                <div class="text-left space-y-4">
                    <div>
                        <label class="text-[10px] font-black text-gray-400 uppercase ml-2">Seleccionar Estudiante</label>
                        <input
                            type="text"
                            id="swal-buscar-nino"
                            class="swal2-input !m-0 !mb-2 !w-full text-sm"
                            placeholder="Seleccionar o buscar estudiante..."
                            autocomplete="off"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-controls="swal-opciones-ninos"
                            aria-expanded="false"
                        >
                        <input type="hidden" id="swal-nino">
                        <div id="swal-opciones-ninos" class="hidden max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg" role="listbox"></div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="text-[10px] font-black text-gray-400 uppercase ml-2">Monto ($)</label>
                            <input type="number" id="swal-monto" class="swal2-input !m-0 !w-full" placeholder="0">
                        </div>
                        <div>
                            <label class="text-[10px] font-black text-gray-400 uppercase ml-2">Días</label>
                            <input type="number" id="swal-dias" class="swal2-input !m-0 !w-full" value="30">
                        </div>
                    </div>
                    <p id="pago-info" class="text-[11px] text-blue-500 font-bold px-2"></p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Registrar Pago',
            confirmButtonColor: '#10b981', // Verde esmeralda (Aceptar)
            cancelButtonColor: '#ef4444',  // Rojo (Cancelar)
            buttonsStyling: true,          // Asegura que use los estilos de Swal y no los del navegador
            customClass: {
                confirmButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#10b981]',
                cancelButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#ef4444]'
            },
            didOpen: () => {
                const searchInput = document.getElementById('swal-buscar-nino');
                const selectedStudentInput = document.getElementById('swal-nino');
                const optionsList = document.getElementById('swal-opciones-ninos');
                const inputMonto = document.getElementById('swal-monto');
                const inputDias = document.getElementById('swal-dias');
                const infoText = document.getElementById('pago-info');

                const renderStudentOptions = () => {
                    const searchTerm = searchInput.value.trim().toLocaleLowerCase('es');
                    const matchingStudents = allNinos.filter(nino =>
                        nino.nombre.toLocaleLowerCase('es').includes(searchTerm)
                    );

                    optionsList.replaceChildren();
                    if (matchingStudents.length === 0) {
                        const noResults = document.createElement('p');
                        noResults.className = 'px-4 py-3 text-sm text-gray-500';
                        noResults.textContent = 'No se encontraron estudiantes.';
                        optionsList.appendChild(noResults);
                        return;
                    }

                    matchingStudents.forEach(nino => {
                        const option = document.createElement('button');
                        option.type = 'button';
                        option.className = 'block w-full px-4 py-3 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none';
                        option.setAttribute('role', 'option');
                        option.textContent = nino.nombre;
                        option.addEventListener('click', () => {
                            selectedStudentInput.value = nino.id;
                            searchInput.value = nino.nombre;
                            searchInput.setAttribute('aria-expanded', 'false');
                            optionsList.classList.add('hidden');
                            inputMonto.value = nino.valorMensualidad || 0;
                            inputDias.value = 30;
                            infoText.textContent = `Sugerido: $${Number(nino.valorMensualidad).toLocaleString('es-CO')}`;
                        });
                        optionsList.appendChild(option);
                    });
                };

                const openOptions = () => {
                    renderStudentOptions();
                    optionsList.classList.remove('hidden');
                    searchInput.setAttribute('aria-expanded', 'true');
                };

                searchInput.addEventListener('click', openOptions);
                searchInput.addEventListener('input', () => {
                    selectedStudentInput.value = '';
                    inputMonto.value = '';
                    infoText.textContent = '';
                    openOptions();
                });
                searchInput.addEventListener('keydown', (event) => {
                    if (event.key === 'Escape') {
                        optionsList.classList.add('hidden');
                        searchInput.setAttribute('aria-expanded', 'false');
                    }
                });
                Swal.getPopup().addEventListener('click', (event) => {
                    if (!optionsList.contains(event.target) && event.target !== searchInput) {
                        optionsList.classList.add('hidden');
                        searchInput.setAttribute('aria-expanded', 'false');
                    }
                });
            },
            preConfirm: () => {
                const ninoId = document.getElementById('swal-nino').value;
                const monto = document.getElementById('swal-monto').value;
                const dias = document.getElementById('swal-dias').value;
                if (!ninoId || !monto) return Swal.showValidationMessage('Datos incompletos');
                return { ninoId, monto: Number(monto), dias: Number(dias) };
            }
        });

        if (formValues) {
            try {
                const nino = allNinos.find(n => n.id === formValues.ninoId);
                const fechaBase = parseLocalDate(nino.proximaFechaPago || new Date());
                const nuevaFechaObj = calcularFechaVencimiento(fechaBase, formValues.dias);
                const nuevaFechaStr = formatDateForStorage(nuevaFechaObj);

                const pagoData = {
                    ninoId: nino.id,
                    ninoNombre: nino.nombre,
                    acudienteNombre: nino.acudientes[0].nombre,
                    acudienteTelefono: nino.acudientes[0].telefono,
                    monto: formValues.monto,
                    fechaEnvio: new Date().toISOString(),
                    estado: 'Aprobado',
                    tipoPago: 'Manual',
                    diasAprobados: formValues.dias
                };

                const pagoRef = await addDoc(collection(db, "pagos_recibidos"), pagoData);

                await updateDoc(doc(db, "ninos", nino.id), {
                    proximaFechaPago: nuevaFechaStr,
                    pagoPendienteVerificacion: false
                });

                // --- Generar factura PDF y subirla a Firebase Storage ---
                let facturaUrl = '';
                try {
                    const pdfBlob = generarFactura(
                        { ...pagoData, id: pagoRef.id },
                        nino,
                        nuevaFechaObj,
                        formValues.dias
                    );
                    const nombreArchivo = generarNombreFactura(nino.nombre, nuevaFechaObj);
                    const storageRef = ref(storage, `facturas/${nombreArchivo}`);
                    await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
                    facturaUrl = await getDownloadURL(storageRef);

                    // Guardar la URL de la factura en el documento del pago
                    await updateDoc(doc(db, "pagos_recibidos", pagoRef.id), { facturaUrl });
                } catch (pdfError) {
                    console.error('Error al generar/subir la factura PDF:', pdfError);
                }

                // --- Enviar WhatsApp de confirmación ---
                const payload = {
                    templateId: 'HXe355079b4dd233f50cb1e7edd281eb84',
                    usuarios: [
                        {
                            telefono: `57${nino.acudientes[0].telefono}`,
                            variables: {
                                "1": nino.acudientes[0].nombre,
                                "2": nino.nombre,
                                "3": `$${Number(formValues.monto).toLocaleString('es-CO')}`,
                                "4": facturaUrl || 'No disponible'
                            }
                        }
                    ]
                };

                enviarPruebaWhatsApp(payload);
                notifySuccess("Éxito", `Pago manual registrado.${facturaUrl ? ' Factura generada y WhatsApp enviado.' : ''}`);
            } catch (error) { notifyError("Error", "No se pudo registrar."); }
        }
    };

    // --- PROCESAMIENTO MASIVO DE FACTURAS MANUALES ---
    const handleBulkGenerateManualInvoices = async () => {
        const manualesPendientes = pagos.filter(p => p.tipoPago === 'Manual' && p.estado === 'Aprobado' && !p.facturaUrl);

        if (manualesPendientes.length === 0) {
            return notifySuccess("Información", "No hay facturas manuales pendientes por procesar.");
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Procesar Facturas Masivas',
            text: `Se generarán y enviarán ${manualesPendientes.length} facturas de pagos manuales. ¿Deseas continuar?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, procesar todos',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#ef4444',
            customClass: {
                confirmButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#10b981]',
                cancelButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#ef4444]'
            },
        });

        if (!isConfirmed) return;

        Swal.fire({
            title: 'Procesando facturas...',
            text: 'Por favor no cierres esta ventana',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
        });

        let exitosos = 0;
        let errores = 0;

        for (const pago of manualesPendientes) {
            try {
                const nino = allNinos.find(n => n.id === pago.ninoId);
                if (!nino) continue;

                const fechaBase = new Date(pago.fechaEnvio);
                fechaBase.setDate(fechaBase.getDate() + (pago.diasAprobados || 30));

                const pdfBlob = generarFactura(
                    pago,
                    nino,
                    fechaBase,
                    pago.diasAprobados || 30
                );
                const nombreArchivo = generarNombreFactura(nino.nombre, fechaBase);
                const storageRef = ref(storage, `facturas/${nombreArchivo}`);
                await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
                const facturaUrl = await getDownloadURL(storageRef);

                await updateDoc(doc(db, "pagos_recibidos", pago.id), { facturaUrl });

                const payload = {
                    templateId: 'HXe355079b4dd233f50cb1e7edd281eb84',
                    usuarios: [
                        {
                            telefono: `57${nino.acudientes[0].telefono}`,
                            variables: {
                                "1": nino.acudientes[0].nombre,
                                "2": nino.nombre,
                                "3": `$${Number(pago.monto).toLocaleString('es-CO')}`,
                                "4": facturaUrl
                            }
                        }
                    ]
                };
                enviarPruebaWhatsApp(payload);
                exitosos++;
            } catch (err) {
                console.error(`Error procesando pago ${pago.id}:`, err);
                errores++;
            }
        }

        Swal.close();
        notifySuccess("Proceso Terminado", `Se procesaron ${exitosos} facturas correctamente.${errores > 0 ? ` Hubo ${errores} errores.` : ''}`);
    };

    // (handleAprobar, handleRechazar y handleLanzarCobranzaManual se mantienen igual que en la versión anterior)
    // 3. Función para Aprobar Pago
    const handleAprobar = async (pago) => {
        // A. Calculamos la fecha base y el valor por defecto (30 días)
        const ninoRef = doc(db, "ninos", pago.ninoId);
        const ninoSnap = await getDoc(ninoRef);

        if (!ninoSnap.exists()) return notifyError("Error", "No se encontró el estudiante.");

        const dataNino = ninoSnap.data();
        const fechaBase = parseLocalDate(dataNino.proximaFechaPago || new Date());

        // Función auxiliar para proyectar la fecha en el modal
        const proyectarFecha = (dias) => {
            const d = calcularFechaVencimiento(fechaBase, parseInt(dias, 10) || 0);
            return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
        };

        // B. Modal interactivo con SweetAlert2
        const { value: diasAdicionales } = await Swal.fire({
            title: 'Aprobar Mensualidad',
            html: `
            <div class="text-left space-y-4 p-2">
                <p class="text-sm text-gray-600">Se añadirán días de cobertura a partir del: <b>${fechaBase.toLocaleDateString()}</b></p>
                <div class="flex flex-col gap-2">
                    <label class="text-xs font-black uppercase text-gray-400">Días a sumar:</label>
                    <input type="number" id="swal-input-days" class="swal2-input !m-0 !w-full" value="30">
                </div>
                <p class="text-sm font-medium text-blue-600 mt-4">
                    Nueva fecha de vencimiento estimada: <br>
                    <span id="fecha-proyectada" class="text-lg font-black">${proyectarFecha(30)}</span>
                </p>
            </div>
        `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Confirmar Aprobación',
            confirmButtonColor: '#10b981', // Verde esmeralda (Aceptar)
            cancelButtonColor: '#ef4444',  // Rojo (Cancelar)
            buttonsStyling: true,          // Asegura que use los estilos de Swal y no los del navegador
            customClass: {
                confirmButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#10b981]',
                cancelButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#ef4444]'
            },
            didOpen: () => {
                const input = Swal.getHtmlContainer().querySelector('#swal-input-days');
                const display = Swal.getHtmlContainer().querySelector('#fecha-proyectada');
                input.addEventListener('input', (e) => {
                    display.innerText = proyectarFecha(e.target.value || 0);
                });
            },
            preConfirm: () => {
                return Swal.getHtmlContainer().querySelector('#swal-input-days').value;
            }
        });

        // C. Ejecución de la lógica de actualización
        if (diasAdicionales) {
            try {
                const diasNum = parseInt(diasAdicionales);
                const nuevaFechaObj = calcularFechaVencimiento(fechaBase, diasNum);
                const nuevaFechaStr = formatDateForStorage(nuevaFechaObj);

                // 1. Actualizar estado del pago
                await updateDoc(doc(db, "pagos_recibidos", pago.id), {
                    estado: 'Aprobado',
                    diasAprobados: diasNum
                });

                // 2. Actualizar al niño
                await updateDoc(ninoRef, {
                    proximaFechaPago: nuevaFechaStr,
                    pagoPendienteVerificacion: false
                });

                // 3. Generar factura PDF y subirla a Firebase Storage
                let facturaUrl = '';
                try {
                    const pdfBlob = generarFactura(
                        { ...pago, id: pago.id },
                        dataNino,
                        nuevaFechaObj,
                        diasNum
                    );
                    const nombreArchivo = generarNombreFactura(dataNino.nombre, nuevaFechaObj);
                    const storageRef = ref(storage, `facturas/${nombreArchivo}`);
                    await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
                    facturaUrl = await getDownloadURL(storageRef);

                    // Guardar la URL de la factura en el documento del pago
                    await updateDoc(doc(db, "pagos_recibidos", pago.id), { facturaUrl });
                } catch (pdfError) {
                    // No bloqueamos la aprobación si la factura falla
                    console.error('Error al generar/subir la factura PDF:', pdfError);
                }

                // 4. Enviar WhatsApp de confirmación (con link de factura como variable #5)
                const payload = {
                    templateId: 'HXe355079b4dd233f50cb1e7edd281eb84',
                    usuarios: [
                        {
                            telefono: `57${dataNino.acudientes[0].telefono}`,
                            variables: {
                                "1": dataNino.acudientes[0].nombre,
                                "2": dataNino.nombre,
                                "3": `$${Number(dataNino.valorMensualidad).toLocaleString('es-CO')}`,
                                "4": facturaUrl || 'No disponible'
                            }
                        }
                    ]
                };

                enviarPruebaWhatsApp(payload);
                notifySuccess("Pago Aprobado", `Mensualidad extendida por ${diasNum} días.${facturaUrl ? ' Factura generada exitosamente.' : ''}`);

            } catch (error) {
                console.error(error);
                notifyError("Error", "No se pudo procesar la aprobación.");
            }
        }
    };

    // 4. Función para Rechazar
    const handleRechazar = async (pago) => {
        const { value: motivo } = await Swal.fire({
            title: 'Rechazar Comprobante',
            input: 'text',
            inputLabel: 'Motivo del rechazo',
            inputPlaceholder: 'Ej: Imagen borrosa, monto incorrecto...',
            showCancelButton: true,
            confirmButtonColor: '#10b981', // Verde esmeralda (Aceptar)
            cancelButtonColor: '#ef4444',  // Rojo (Cancelar)
            buttonsStyling: true,          // Asegura que use los estilos de Swal y no los del navegador
            customClass: {
                confirmButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#10b981]',
                cancelButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#ef4444]'
            },
        });
        console.log("data eli", pago)
        if (motivo) {
            try {
                await updateDoc(doc(db, "pagos_recibidos", pago.id), {
                    estado: 'Rechazado',
                    motivoRechazo: motivo
                });
                // guasa
                const payload = {
                    templateId: 'HX51e1d39a0152eed54641ab84a5d7736e',
                    usuarios: [
                        {
                            telefono: `57${pago.acudienteTelefono}`,
                            variables: {
                                "1": pago.acudienteNombre,
                                "2": pago.ninoNombre,
                                "3": motivo,
                                "4": `$${Number(pago.monto).toLocaleString('es-CO')}`,
                                "5": pago.id
                            }
                        }
                    ]
                };

                enviarPruebaWhatsApp(payload)
                notifySuccess("Rechazado", "Se ha notificado el rechazo.");
            } catch (error) {
                notifyError("Error", "No se pudo actualizar el estado.");
            }
        }
    };



    const handleLanzarCobranzaManual = async () => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Lanzar cobranza del día?',
            text: "Se enviarán recordatorios a todos los padres con pagos próximos o vencidos según la lógica de 5, 3, 1 y 0 días.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3b82f6',
            confirmButtonText: 'Sí, enviar ahora',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981', // Verde esmeralda (Aceptar)
            cancelButtonColor: '#ef4444',  // Rojo (Cancelar)
            buttonsStyling: true,          // Asegura que use los estilos de Swal y no los del navegador
            customClass: {
                confirmButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#10b981]',
                cancelButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#ef4444]'
            },
        });

        if (isConfirmed) {
            // Mostrar cargando
            Swal.fire({
                title: 'Procesando envíos...',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
            });

            try {
                const functions = getFunctions();
                const ejecutarCobranza = httpsCallable(functions, 'ejecutarCobranzaManual');

                const result = await ejecutarCobranza();

                Swal.close();
                notifySuccess("Proceso Terminado", `Se procesaron ${result.data.procesados} mensajes exitosamente.`);
            } catch (error) {
                console.error(error);
                Swal.close();
                notifyError("Error", "No se pudo ejecutar la cobranza manual.");
            }
        }
    };

    if (loading) return <div className="p-10 sm:p-20 text-center animate-pulse font-bold text-blue-600">Cargando pagos...</div>;

    return (
        <div className="space-y-4 sm:space-y-6 pb-6 sm:pb-10 pl-2 lg:pl-10 pr-2 lg:pr-14">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="w-full md:w-auto text-center md:text-left">
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-800 tracking-tight">Gestión de Pagos</h1>
                    <p className="text-sm sm:text-base text-gray-500 font-medium">Filtra y valida los recaudos del jardín.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto">
                    {pagos.some(p => p.tipoPago === 'Manual' && p.estado === 'Aprobado' && !p.facturaUrl) && (
                        <button onClick={handleBulkGenerateManualInvoices} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 sm:px-6 rounded-2xl font-bold shadow-lg transition-all active:scale-95 text-sm sm:text-base">
                            <FileText size={18} /> Facturar Manuales ({pagos.filter(p => p.tipoPago === 'Manual' && p.estado === 'Aprobado' && !p.facturaUrl).length})
                        </button>
                    )}
                    <button onClick={handlePagoManual} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 sm:px-6 rounded-2xl font-bold shadow-lg transition-all active:scale-95 text-sm sm:text-base">
                        <PlusCircle size={18} /> Pago Manual
                    </button>
                    <button onClick={handleLanzarCobranzaManual} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 sm:px-6 rounded-2xl font-bold shadow-lg transition-all active:scale-95 text-sm sm:text-base">
                        <Send size={18} /> Cobranza del Día
                    </button>
                </div>
            </header>

            {/* --- SECCIÓN DE FILTROS AVANZADOS --- */}
            <div className="bg-white p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="Buscar estudiante..."
                        className="w-full pl-11 pr-4 py-3 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium border-transparent"
                        value={filtroNombre}
                        onChange={(e) => setFiltroNombre(e.target.value)}
                    />
                </div>

                <div className="relative">
                    <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <select
                        className="w-full pl-11 pr-4 py-3 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 text-sm font-bold text-gray-600 border-transparent appearance-none"
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                    >
                        <option value="todos">Todos los estados</option>
                        <option value="Pendiente">Solo Pendientes</option>
                        <option value="Aprobado">Aprobados</option>
                        <option value="Rechazado">Rechazados</option>
                    </select>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-2 sm:col-span-2 lg:col-span-2">
                    <div className="w-full flex-1 relative">
                        <input
                            type="date"
                            className="w-full px-4 py-3 bg-gray-50 rounded-2xl text-xs sm:text-sm font-bold border-transparent outline-none focus:ring-2 focus:ring-blue-500"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                        <span className="absolute -top-2 left-4 bg-white px-1 text-[9px] font-black text-gray-400 uppercase">Inicio</span>
                    </div>
                    <div className="w-full flex-1 relative mt-2 sm:mt-0">
                        <input
                            type="date"
                            className="w-full px-4 py-3 bg-gray-50 rounded-2xl text-xs sm:text-sm font-bold border-transparent outline-none focus:ring-2 focus:ring-blue-500"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                        <span className="absolute -top-2 left-4 bg-white px-1 text-[9px] font-black text-gray-400 uppercase">Fin</span>
                    </div>
                    {(fechaInicio || fechaFin || filtroNombre || filtroEstado !== 'todos') && (
                        <button
                            onClick={() => { setFiltroNombre(''); setFiltroEstado('todos'); setFechaInicio(''); setFechaFin(''); }}
                            className="p-3 w-full sm:w-auto text-red-500 hover:bg-red-50 bg-red-50 sm:bg-transparent rounded-2xl transition-colors mt-2 sm:mt-0 flex justify-center items-center"
                            title="Limpiar filtros"
                        >
                            <X size={20} className="hidden sm:block" />
                            <span className="sm:hidden font-bold flex items-center gap-2"><X size={16}/> Limpiar filtros</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[600px]">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100">
                                <th className="px-4 sm:px-6 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest text-center">Fecha</th>
                                <th className="px-4 sm:px-6 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Estudiante</th>
                                <th className="px-4 sm:px-6 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Monto (TOTAL: ${Number(totalPagos).toLocaleString("es-CO")})</th>
                                <th className="px-4 sm:px-6 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest text-center">Estado</th>
                                <th className="px-4 sm:px-6 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {currentPagos.map((pago) => (
                                <tr key={pago.id} className="hover:bg-blue-50/20 transition-colors group">
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 text-center">
                                        <p className="text-xs sm:text-sm font-bold text-gray-600 whitespace-nowrap">{new Date(pago.fechaEnvio).toLocaleDateString()}</p>
                                        <p className="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase">{pago.tipoPago || 'Digital'}</p>
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5">
                                        <p className="text-sm sm:text-base font-bold text-gray-800 line-clamp-1">{pago.ninoNombre}</p>
                                        <p className="text-[10px] sm:text-xs text-gray-400 line-clamp-1">{pago.acudienteNombre}</p>
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 text-sm sm:text-base font-black text-gray-700 whitespace-nowrap">
                                        ${Number(pago.monto || 0).toLocaleString('es-CO')}
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 text-center">
                                        <span className={`text-[9px] sm:text-[10px] font-black px-2 sm:px-3 py-1 rounded-full uppercase whitespace-nowrap ${pago.estado === 'Aprobado' ? 'bg-green-100 text-green-600' :
                                            pago.estado === 'Rechazado' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                                            }`}>
                                            {pago.estado}
                                        </span>
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 text-right">
                                        <div className="flex justify-end gap-1 sm:gap-2">
                                            {pago.comprobanteUrl && (
                                                <button onClick={() => { setSelectedImg(pago.comprobanteUrl); setSelectedMotivo(pago.motivoRechazo); }} className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors" title="Ver comprobante">
                                                    <Eye size={16} className="sm:w-[18px] sm:h-[18px]" />
                                                </button>
                                            )}
                                            {pago.facturaUrl && (
                                                <a
                                                    href={pago.facturaUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors"
                                                    title="Ver factura PDF"
                                                >
                                                    <FileText size={16} className="sm:w-[18px] sm:h-[18px]" />
                                                </a>
                                            )}
                                            {pago.estado === 'Pendiente' && (
                                                <>
                                                    <button onClick={() => handleAprobar(pago)} className="p-1.5 sm:p-2 text-white bg-green-500 hover:bg-green-600 rounded-xl shadow-md transition-colors" title="Aprobar pago"><Check size={16} className="sm:w-[18px] sm:h-[18px]" /></button>
                                                    <button onClick={() => handleRechazar(pago)} className="p-1.5 sm:p-2 text-white bg-red-500 hover:bg-red-600 rounded-xl shadow-md transition-colors" title="Rechazar pago"><X size={16} className="sm:w-[18px] sm:h-[18px]" /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {currentPagos.length === 0 && (
                        <div className="p-6 sm:p-10 text-center text-gray-400 font-medium text-sm sm:text-base">
                            No se encontraron pagos con los filtros actuales.
                        </div>
                    )}
                </div>

                <div className="p-4 sm:p-6 bg-gray-50/50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-[10px] sm:text-xs text-gray-400 font-bold uppercase order-2 sm:order-1">Página {currentPage} de {totalPages || 1}</p>
                    <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto justify-between sm:justify-end">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} className="p-2 bg-white border border-gray-200 rounded-xl disabled:opacity-30 flex-1 sm:flex-none flex justify-center"><ChevronLeft size={20} /></button>
                        <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => prev + 1)} className="p-2 bg-white border border-gray-200 rounded-xl disabled:opacity-30 flex-1 sm:flex-none flex justify-center"><ChevronRight size={20} /></button>
                    </div>
                </div>
            </div>

            {/* MODAL IMAGEN (Igual que antes) */}
            {selectedImg && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-gray-900/80 backdrop-blur-sm" onClick={() => setSelectedImg(null)}>
                    <div className="relative w-full max-w-2xl bg-white rounded-[2rem] sm:rounded-[3rem] overflow-hidden p-2 shadow-2xl animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                        <div className="absolute top-4 right-4 z-10">
                            <button onClick={() => setSelectedImg(null)} className="p-2 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-md">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="bg-gray-100 rounded-[1.5rem] sm:rounded-[2.5rem] overflow-hidden flex items-center justify-center min-h-[300px]">
                            <img src={selectedImg} alt="Comprobante" className="w-full h-auto max-h-[70vh] object-contain" />
                        </div>
                        <div className="p-4 sm:p-6 text-center">
                            {selectedMotivo && <p className="text-xs sm:text-sm text-red-500 mb-2 font-medium bg-red-50 p-3 rounded-xl">Motivo rechazo: {selectedMotivo}</p>}
                            <a href={selectedImg} target="_blank" rel="noreferrer" className="text-blue-600 text-sm sm:text-base font-bold hover:underline flex items-center justify-center gap-2">
                                <ExternalLink size={16} /> Ver imagen original en nueva pestaña
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionPagos;